<template>
    <v-container class="profil px-0 pt-3 pb-0">
        <v-card class="px-2 pt-2 pb-5 mb-2"
                flat
                tile>

            <!-- Profile -->
            <v-row no-gutters
                   align="center">
                <v-col cols="2"
                       class="text-right">
                    <user-avatar :size="25"
                                 :user="currentUser"></user-avatar>
                </v-col>
                <v-col cols="10"
                       class="pl-4">
                    <h2 v-if="currentUser"
                        class="headline font-weight-bold">{{ currentUser.first_name }} {{ currentUser.last_name }}</h2>
                </v-col>
                <v-col v-if="currentUser && currentUser.profile"
                       offset="2"
                       cols="9"
                       class="pl-4">

                    <p class="title subtitle-1">
                        <span class="info--text">{{ $vuetify.lang.t('$vuetify.form_items.participant_number') }}:</span>
                        {{ currentUser.participant_number }}
                    </p>
                    <h5 class="mb-1 subtitle-1 font-weight-bold">
                        {{ $vuetify.lang.t('$vuetify.profile.private_data') }}:
                    </h5>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.street') }}:</span>
                        {{ currentUser.profile.private_address_street }} {{
                            currentUser.profile.private_address_number
                        }}
                    </p>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.zip_code') }}:</span>
                        {{ currentUser.profile.private_address_zip }}
                    </p>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.city') }}:</span>
                        {{ currentUser.profile.private_address_city }}
                    </p>
                    <p class="ma-0 subtitle-1">
                        <span :class="{ 'text--secondary': currentUser.profile.private_address_country, 'text--error': !currentUser.profile.private_address_country }">{{
                                $vuetify.lang.t('$vuetify.form_items.country')
                                                                                                                                                                       }}:</span>
                        {{ currentUser.profile.private_address_country }}
                    </p>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.profile.phone') }}:</span>
                        {{ currentUser.profile.private_phone }}
                    </p>
                    <p class="mb-4 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.email') }}:</span>
                        {{ currentUser.email }}
                    </p>
                    <h5 class="mb-1 subtitle-1 font-weight-bold">
                        {{ $vuetify.lang.t('$vuetify.profile.business_data') }}:
                    </h5>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.profile.phone') }}:</span>
                        {{ currentUser.profile.business_phone }}
                    </p>
                    <p class="mb-4 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.email') }}:</span>
                        {{ currentUser.profile.business_email }}
                    </p>
                </v-col>
                <v-col offset="2"
                       cols="9"
                       class="pl-4 mb-2">
                    <v-btn color="primary darken-1"
                           :to="changeDataUrl">
                        {{ $vuetify.lang.t('$vuetify.buttons.update_data') }}
                    </v-btn>
                </v-col>
                <v-col offset="2"
                       cols="9"
                       class="pl-4 mb-2">
                    <v-btn color="primary darken-1"
                           :to="changeAvatarUrl">
                        {{ $vuetify.lang.t('$vuetify.buttons.change_avatar') }}
                    </v-btn>
                </v-col>
                <v-col offset="2"
                       cols="9"
                       class="pl-4">
                    <v-btn color="primary darken-1"
                           :disabled="loadingResetPassword"
                           :loading="loadingResetPassword"
                           @click="resetPassword">
                        {{ $vuetify.lang.t('$vuetify.buttons.change_pw') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-card>

        <!-- Company data -->
        <v-card class="px-2 pt-2 pb-5 mb-2"
                flat
                tile>
            <v-row no-gutters
                   align="center">
                <v-col cols="2"
                       class="text-right">
                    <v-icon>mdi-caravan</v-icon>
                </v-col>
                <v-col cols="9"
                       class="pl-4">
                    <h2 class="headline font-weight-bold">
                        <template v-if="this.currentUser.role === 'owner'">
                            {{ $vuetify.lang.t('$vuetify.profile.your_company') }}
                        </template>
                        <template v-else>
                            {{ $vuetify.lang.t('$vuetify.profile.your_retailer') }}
                        </template>
                    </h2>
                </v-col>
                <v-col v-if="currentUser && currentUser.company"
                       offset="2"
                       cols="9"
                       class="pl-4 mt-4">
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.company') }}:</span>
                        {{ currentUser.company.name }}
                    </p>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.company_id') }}:</span>
                        {{ currentUser.company.company_id }}
                    </p>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.street') }}:</span>
                        {{ currentUser.company.address_street }} {{ currentUser.company.address_number }}
                    </p>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.zip_code') }}:</span>
                        {{ currentUser.company.address_zip }}
                    </p>
                    <p class="ma-0 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.city') }}:</span>
                        {{ currentUser.company.address_city }}
                    </p>
                    <p class="mb-4 subtitle-1">
                        <span class="secondary--text">{{ $vuetify.lang.t('$vuetify.form_items.country') }}:</span>
                        {{ currentUser.company.country.country_code }}
                    </p>
                    <template v-if="currentUser.role === 'owner'">
                        <p class="ma-0 subtitle-1">
                            <span class="secondary--text"
                                  v-html="`${$vuetify.lang.t('$vuetify.form_items.terms_and_conditions')}: `"></span>
                            <span class="success--text"
                                  v-if="!!currentUser.terms_and_conditions"
                                  v-html="$vuetify.lang.t('$vuetify.form_items.terms_and_conditions_accepted')">
                            </span>
                            <span class="error--text"
                                  v-else
                                  v-html="$vuetify.lang.t('$vuetify.form_items.terms_and_conditions_not_accepted')">
                            </span>

                        </p>
                        <p class="ma-0 subtitle-1">
                            <span class="secondary--text"
                                  v-html="`${$vuetify.lang.t('$vuetify.form_items.taxation_documentation')}: `"></span>
                            <span v-if="currentUser.company.email_taxation_documentation"
                                  v-html="currentUser.company.email_taxation_documentation"></span>
                            <span v-else
                                  v-html="$vuetify.lang.t('$vuetify.form_items.taxation_documentation_not_entered')"></span>
                        </p>
                        <p class="subtitle-1"
                           v-if="!currentUser.company.email_taxation_documentation_verified_at && currentUser.company.email_taxation_documentation">
                            <span class="secondary--text"
                                  v-html="`${$vuetify.lang.t('$vuetify.form_items.taxation_documentation_verified_at')}: `"></span>

                            <span class="success--text"
                                  v-if="currentUser.company.email_taxation_documentation_verified_at"
                                  v-html="currentUser.company.email_taxation_documentation_verified_at"></span>
                            <span class="error--text"
                                  v-else
                                  v-html="$vuetify.lang.t('$vuetify.form_items.taxation_documentation_not_verified')"></span>
                        </p>

                        <div class="error--text mt-4 mb-4"
                             v-html="$vuetify.lang.t('$vuetify.form_items.taxation_documentation_hint')"></div>
                    </template>

                    <!--<v-btn v-if="currentUser && currentUser.role && currentUser.role !== 'owner'"-->
                    <!--color="primary darken-1"-->
                    <!--:to="changeRetailerUrl">-->
                    <!--{{ $vuetify.lang.t('$vuetify.buttons.change_retailer') }}-->
                    <!--</v-btn>-->
                </v-col>
                <v-col v-if="!currentUser.company.email_taxation_documentation_verified_at && currentUser.company.email_taxation_documentation"
                       offset="2"
                       cols="9"
                       class="pl-4 mb-2">

                    <v-btn color="primary darken-1"
                           @click.native.prevent="handleResendVerification">
                        {{ $vuetify.lang.t('$vuetify.buttons.resend_verification') }}
                    </v-btn>
                </v-col>
                <v-col v-if="currentUser.role === 'owner'"
                       offset="2"
                       cols="9"
                       class="pl-4 mb-2">

                    <v-btn color="primary darken-1"
                           :to="changeCompanyDataUrl">
                        {{ $vuetify.lang.t('$vuetify.buttons.update_data') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-card>

        <!-- Settings -->
        <v-card class="px-2 pt-2 pb-5 pb-0 mb-2"
                flat
                tile>
            <v-row no-gutters
                   align="center">
                <v-col cols="2"
                       class="text-right">
                    <v-icon>mdi-cog</v-icon>
                </v-col>
                <v-col cols="9"
                       class="pl-4">
                    <h2 class="headline font-weight-bold">
                        {{ $vuetify.lang.t('$vuetify.profile.settings') }}
                    </h2>
                </v-col>
                <v-col offset="2"
                       cols="9"
                       class="pl-4 mt-2">
                    <h4 class="subtitle-1 font-weight-bold mb-2">
                        {{ $vuetify.lang.t('$vuetify.form_items.application_language') }}:</h4>
                    <v-select v-if="currentUser"
                              filled
                              dense
                              :items="localeSelect"
                              :value="currentUser.locale"
                              :loading="loadingChangeLocale"
                              :disabled="loadingChangeLocale"
                              @change="changeLocale"></v-select>

<!--                  commented out because of ticket MSUITE-2661, but kept in so if functionality is wanted again later-->
                  <!--                    <h4 class="subtitle-1 font-weight-bold mb-2">
                                          {{ $vuetify.lang.t('$vuetify.form_items.tracking_toggle') }}:</h4>
                                      <p v-if="currentUser && currentUser.profile && currentUser.profile.tracking"
                                         class="success&#45;&#45;text mb-0">
                                          {{ $vuetify.lang.t('$vuetify.form_items.tracking_active') }}
                                      </p>
                                      <p v-else
                                         class="error&#45;&#45;text mb-0">
                                          {{ $vuetify.lang.t('$vuetify.form_items.tracking_inactive') }}
                                      </p>
                                      <div v-if="allowToggleTracking">
                                          <v-btn v-if="currentUser && currentUser.profile && currentUser.profile.tracking"
                                                 class="mt-2"
                                                 @click="toggleTracking"
                                                 color="error"
                                                 depressed
                                                 :loading="loadingChangeTracking"
                                                 :disabled="loadingChangeTracking">
                                              {{ $vuetify.lang.t('$vuetify.buttons.disable_tracking') }}
                                          </v-btn>
                                          <v-btn v-else
                                                 class="mt-2"
                                                 @click="toggleTracking"
                                                 color="success"
                                                 depressed
                                                 :loading="loadingChangeTracking"
                                                 :disabled="loadingChangeTracking">
                                              {{ $vuetify.lang.t('$vuetify.buttons.enable_tracking') }}
                                          </v-btn>
                                      </div>-->
                </v-col>
            </v-row>
        </v-card>

        <!-- Account -->
        <v-card class="px-2 pt-2 pb-5 pb-0 mb-2"
                flat
                tile>
            <v-row no-gutters
                   align="center">
                <v-col cols="2"
                       class="text-right">
                    <v-icon>mdi-security</v-icon>
                </v-col>
                <v-col cols="9"
                       class="pl-4">
                    <h2 class="headline font-weight-bold">
                        {{ $vuetify.lang.t('$vuetify.profile.account') }}
                    </h2>
                </v-col>
                <v-col offset="2"
                       cols="9"
                       class="pl-4 mt-2">
                    <h4 class="subtitle-1 font-weight-bold mb-2"
                        v-html="`${$vuetify.lang.t('$vuetify.profile.delete_account')}:`"></h4>
                    <i18n class="mb-0"
                          tag="p"
                          path="$vuetify.profile.delete_account_desc">
                        <template v-slot:0>
                            <static-content-dialog slug="data_protection">
                                <a href="#" @click.prevent>{{ $vuetify.lang.t(`$vuetify.form_items.data_protection_link`) }}</a>
                            </static-content-dialog>
                        </template>
                    </i18n>
                    <v-btn class="mt-2"
                           @click="deleteAccountConfirmation = true"
                           color="error"
                           depressed
                           :loading="loadingDeleteAccount"
                           :disabled="loadingDeleteAccount">
                        {{ $vuetify.lang.t('$vuetify.buttons.delete_account') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-card>

        <v-dialog v-model="deleteAccountConfirmation"
                  persistent
                  width="500">
            <v-card>
                <v-card-text class="pt-6">
                    <h3 class="mb-4"
                        v-html="$vuetify.lang.t('$vuetify.profile.delete_account_confirmation_title')">
                    </h3>
                    <p class="mb-4">
                        <strong>{{ $vuetify.lang.t('$vuetify.profile.delete_account_confirmation_hint') }}</strong>
                    </p>
                    <p>
                        {{ $vuetify.lang.t('$vuetify.profile.delete_account_confirmation_desc') }}
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary"
                           variant="text"
                           @click="deleteAccountConfirmation = false">
                        {{ $vuetify.lang.t('$vuetify.buttons.cancel') }}
                    </v-btn>
                    <v-btn color="error"
                           depressed
                           variant="text"
                           @click="handleDeleteAccount">
                        {{ $vuetify.lang.t('$vuetify.buttons.delete') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Debug window -->
        <v-card class="px-2 pt-2 pb-5 mb-2"
                v-if="isTesting"
                flat
                tile>
            <v-row no-gutters
                   align="center">
                <v-col cols="2"
                       class="text-right">
                    <v-icon>mdi-test-tube</v-icon>
                </v-col>
                <v-col cols="9"
                       class="pl-4">
                    <h2 class="headline font-weight-bold">
                        Debug
                    </h2>
                </v-col>
                <v-col offset="2"
                       cols="9"
                       class="pl-4 mt-2">
                    <v-btn color="primary darken-1"
                           @click="triggerError">
                        cause an error to test sentry
                    </v-btn>
                </v-col>
            </v-row>

            <v-row no-gutters
                   align="center"
                   v-if="isDebugAndApp">
                <v-col offset="2"
                       cols="9"
                       class="pl-4">
                    <h2 class="headline font-weight-bold">
                        Firebase & Push Notifications
                    </h2>
                </v-col>
                <v-col offset="2"
                       cols="9"
                       class="pl-4 mt-8 ">

                    <v-btn @click.prevent="reevaluateToken">reevaluate token</v-btn>
                    <div>
                        <div>FCM Token: {{ deviceToken }}</div>
                        <div>UUID: {{ deviceUuid }}</div>
                    </div>

                </v-col>
            </v-row>
        </v-card>
    </v-container>
</template>
<script>
// 3rd
import {
    mapActions,
    mapState,
} from 'vuex';
// utils
import {
    evaluatePlatform,
    initDeviceToken,
    setPageTitle,
    transformArrayToSelectValues,
} from '../utils/helper';
import {
    LOADER_CHANGE_LOCALE,
    LOADER_CHANGE_TRACKING, LOADER_DELETE_ACCOUNT,
    LOADER_RESET_PASSWORD,
    ROUTE_CHANGE_AVATAR,
    ROUTE_CHANGE_COMPANY_DATA,
    ROUTE_CHANGE_DATA,
    ROUTE_CHANGE_RETAILER,
} from '../utils/constants';
// models
import User from '../models/User';
import Profile from '../models/Profile';
// components
import UserAvatar from '../components/UserAvatar';
import {logOutUser} from '../utils/helper';
import StaticContentDialog from '../components/StaticContentDialog.vue';

export default {
    name: 'profile',
    data() {
        return {
            deleteAccountConfirmation: false,
        };
    },
    components: {
        UserAvatar,
        StaticContentDialog,
    },
    computed: {
        ...mapState([
            'locales',
            'deviceToken',
            'deviceUuid',
        ]),
        isDebugAndApp() {
            return !!(evaluatePlatform() === 'app' && this.isTesting);
        },
        isTesting() {
            return process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'testing';
        },
        localeSelect() {
            return transformArrayToSelectValues(this.currentUser.languages.map(l => l.name), '$vuetify.locale.');
        },
        currentUser() {
            return User.getCurrent();
        },
        loadingResetPassword() {
            return this.$wait.is(LOADER_RESET_PASSWORD);
        },
        loadingDeleteAccount() {
            return this.$wait.is(LOADER_DELETE_ACCOUNT);
        },
        loadingChangeTracking() {
            return this.$wait.is(LOADER_CHANGE_TRACKING);
        },
        loadingChangeLocale() {
            return this.$wait.is(LOADER_CHANGE_LOCALE);
        },
        changeRetailerUrl() {
            return {name: ROUTE_CHANGE_RETAILER};
        },
        changeDataUrl() {
            return {name: ROUTE_CHANGE_DATA};
        },
        changeCompanyDataUrl() {
            return {name: ROUTE_CHANGE_COMPANY_DATA};
        },
        changeAvatarUrl() {
            return {name: ROUTE_CHANGE_AVATAR};
        },

      // commented out because of ticket MSUITE-2661, but kept in so if functionality is wanted again later
      /*  allowToggleTracking() {
            if (typeof device !== 'undefined') {
                return !(device && device.platform === 'iOS' && device.version >= '14.5');
            }
            return true;
        },*/
    },
    methods: {
        ...mapActions([
            'setSnackbar',
        ]),
        async handleResendVerification() {
            const result = await Profile.resendVerificationMail();

            this.setSnackbar({
                positionX: 'left',
                type: 'success',
                translatedText: result.data.message,
                time: 5000,
            });
        },
        reevaluateToken() {
            initDeviceToken();
        },
        triggerError() {
            throw new Error('If this Error occurs in Sentry, it\'s working!');
        },
        async resetPassword() {
            const u = User.getCurrent();

            const form = {
                email: u.email,
            };
            const pwReset = await User.resetPassword(form);

            if (pwReset.status === 200) {
                this.setSnackbar({
                    positionX: 'left',
                    type: 'success',
                    text: 'change_password_mail_sent',
                    time: 5000,
                });
            }
        },
        async changeLocale(value) {
            await User.setLocale(value);

            this.setSnackbar({
                positionX: 'left',
                type: 'success',
                text: 'updated_locale',
                time: 5000,
            });
        },
        async handleDeleteAccount() {
            const result = await Profile.deleteAccount();

            this.setSnackbar({
                positionX: 'left',
                type: 'success',
                translatedText: result,
                time: 10000,
            });

            logOutUser();
        },
      // commented out because of ticket MSUITE-2661, but kept in so if functionality is wanted again later
      /*  async toggleTracking() {
            await Profile.setTracking();

            this.setSnackbar({
                positionX: 'left',
                type: 'success',
                text: 'updated_tracking',
                time: 5000,
            });
        },*/
    },
};
</script>
